/**
 * Send a render crash to the server, so that it leaves a record somewhere
 * other than the browser console.
 *
 * Deliberately NOT the axios instance. That one carries interceptors — the
 * emails-sent broadcaster, error normalisation, whatever is added next — and a
 * crash reporter must not depend on the machinery that may itself be what
 * crashed. `fetch` with no wrapper is the whole point.
 *
 * Three rules, each of which has an obvious failure mode if broken:
 *
 *  1. It can never throw. The caller is `componentDidCatch`; an exception
 *     there escapes React's error handling entirely.
 *  2. It can never produce an unhandled rejection. ToastProvider now listens
 *     for those, so a failed report would put "Something went wrong" on top of
 *     the crash screen — the app apologising twice for one fault.
 *  3. It never retries. A crash loop that reports every iteration turns one
 *     bug into a flood, and the tenth report says nothing the first did not.
 */

const ENDPOINT = `${import.meta.env.VITE_API_URL || '/api'}/client-errors`

/** Belt and braces: the server truncates too, but do not send what will be cut. */
function cap(value: string | null | undefined, limit: number): string | undefined {
  if (!value) return undefined
  return value.length > limit ? value.slice(0, limit) : value
}

export function reportClientError(error: Error, componentStack?: string | null): void {
  try {
    const body = JSON.stringify({
      message: cap(error?.message, 1000) ?? 'Unknown error',
      stack: cap(error?.stack, 8000),
      component_stack: cap(componentStack, 8000),
      url: cap(typeof window !== 'undefined' ? window.location.pathname : null, 500),
      user_agent: cap(typeof navigator !== 'undefined' ? navigator.userAgent : null, 300),
      app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,
    })

    // keepalive so the report survives the navigation that often follows a
    // crash — including the reader hitting "Back to home".
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      // Rule 2. Nothing to do about a failed crash report, and saying so on
      // screen would be the app apologising twice for one fault.
    })
  } catch {
    // Rule 1. JSON.stringify on a circular error, fetch missing in a test
    // environment — none of it is worth turning into a second failure.
  }
}
